// src/pages/ResultsPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { getAllResults, getResultsDetails, exportSelectedResults, deleteSelectedResults, createConclusion, downloadConclusionPdf, saveConclusionPdf } from '../api/results';
import { Button } from '../components/common/Button';
import ResultDetailView from './ResultDetailView';
import type { ResultRow, ResultDetail } from '../api/types';
import { useLang } from '../context/LangContext';
import { getPublicResultsEnabled, setPublicResultsEnabled } from '../api/settings';

type ColKey = 'id' | 'test' | 'username' | 'name' | 'email' | 'device' | 'age' | 'gender' | 'time_spent' | 'completed_at' | 'share_code' | 'pdf' | 'payment' | 'downloads';

const ALL_COLS: ColKey[] = ['id', 'test', 'username', 'name', 'email', 'device', 'age', 'gender', 'time_spent', 'completed_at', 'share_code', 'pdf', 'payment', 'downloads'];
const DEFAULT_VISIBLE = new Set<ColKey>(['id', 'test', 'username', 'name', 'email', 'time_spent', 'completed_at', 'share_code', 'pdf', 'payment', 'downloads']);

const PageContainer = styled.div`
  min-height: 100vh;
  padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.lg}`};
  padding-bottom: 64px;
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const StyledSelect = styled.select`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.cardBackground};
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.9rem;
  max-width: 220px;
`;

const SearchInput = styled.input`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.cardBackground};
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.9rem;
  width: 220px;
`;

const ColToggleWrapper = styled.div`
  position: relative;
  margin-left: auto;
`;

const SettingToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textLight};
  cursor: pointer;
  user-select: none;
  input {
    accent-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ColToggleMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm};
  z-index: 100;
  min-width: 170px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const ColToggleItem = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => `${theme.spacing.xs} 0`};
  cursor: pointer;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  user-select: none;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  min-height: 200px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.cardBackground};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  text-align: left;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textLight};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
  z-index: 1;
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.md}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tr = styled.tr<{ $selected?: boolean }>`
  background: ${({ $selected, theme }) =>
    $selected ? `${theme.colors.primary}18` : 'transparent'};
  cursor: pointer;
  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? `${theme.colors.primary}28` : theme.colors.background};
  }
`;

const FixedBottomBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: ${({ theme }) => theme.colors.cardBackground};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

const ScrollTopButton = styled.button<{ $visible: boolean }>`
  position: fixed;
  bottom: 72px;
  right: 24px;
  z-index: 101;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.25s, filter 0.15s;

  &:hover {
    filter: brightness(0.88);
  }
`;

const StatusText = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-right: auto;
`;

/* ── Toast ── */
const ToastMsg = styled.div`
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  padding: 12px 24px;
  border-radius: 8px;
  background: #333;
  color: #fff;
  font-size: 0.9rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  animation: fadeInUp 0.3s ease-out;
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateX(-50%) translateY(12px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

/* ── Confirm modal ── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;
`;

const ConfirmModal = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 420px;
  width: 90%;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const ResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const dateLocale = lang === 'ru' ? 'ru' : 'en';

  const COL_LABELS: Record<ColKey, string> = {
    id: t('resultsColId'),
    test: t('resultsColTest'),
    username: t('resultsColUser'),
    name: t('resultsColName'),
    email: 'Email',
    device: t('resultsColDevice'),
    age: t('resultsColAge'),
    gender: t('resultsColGender'),
    time_spent: t('resultsColTime'),
    completed_at: t('resultsColDate'),
    share_code: t('resultsColShareCode'),
    pdf: t('resultsColPdf'),
    payment: t('resultsColPayment'),
    downloads: t('resultsColDownloads'),
  };
  const [results, setResults] = useState<ResultRow[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loadError, setLoadError] = useState('');
  const [resultsPage, setResultsPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [visibleCols, setVisibleCols] = useState<Set<ColKey>>(new Set(DEFAULT_VISIBLE));
  const [showColMenu, setShowColMenu] = useState(false);
  const [testFilter, setTestFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [toastMsg, setToastMsg] = useState('');
  const [confirmState, setConfirmState] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const checkboxAllRef = useRef<HTMLInputElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [detailMode, setDetailMode] = useState(false);
  const [detailData, setDetailData] = useState<ResultDetail[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [withParams, setWithParams] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [conclusionLoading, setConclusionLoading] = useState(false);
  const [publicResultsEnabled, setPublicResultsEnabledState] = useState(true);
  const [publicResultsLoading, setPublicResultsLoading] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await getPublicResultsEnabled();
        setPublicResultsEnabledState(res.enabled);
      } catch {
        // оставляем включено по умолчанию
      }
    })();
  }, []);

  useEffect(() => {
    getAllResults(1)
      .then(({ items, total }) => { setResults(items); setTotalResults(total); })
      .catch(() => setLoadError(t('resultsLoadError')))
      .finally(() => setLoading(false));
  }, []);

  // Автообновление таблицы каждые 30 секунд: админ видит свежие счётчики
  // скачиваний и статусы оплат без F5. Тихое (без спиннера) и только когда
  // вкладка активна и не идёт другое действие с таблицей.
  const refreshResults = React.useCallback(async () => {
    try {
      const pages = await Promise.all(
        Array.from({ length: Math.max(resultsPage, 1) }, (_, i) => getAllResults(i + 1)),
      );
      setResults(pages.flatMap(p => p.items));
      setTotalResults(pages[0]?.total ?? 0);
    } catch {
      // при сбое сети просто ждём следующего тика — таблицу не трогаем
    }
  }, [resultsPage]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (
        document.visibilityState === 'visible' &&
        !detailMode && !exportLoading && !deleteLoading && !conclusionLoading
      ) {
        void refreshResults();
      }
    }, 30000);
    return () => window.clearInterval(timer);
  }, [refreshResults, detailMode, exportLoading, deleteLoading, conclusionLoading]);

  const handleLoadMore = async () => {
    const nextPage = resultsPage + 1;
    setLoadingMore(true);
    try {
      const { items, total } = await getAllResults(nextPage);
      setResults(prev => [...prev, ...items]);
      setTotalResults(total);
      setResultsPage(nextPage);
    } finally {
      setLoadingMore(false);
    }
  };

  const uniqueTests = useMemo(() => {
    const map = new Map<number, string>();
    results.forEach(r => {
      if (r.test_id != null && !map.has(r.test_id))
        map.set(r.test_id, r.test_title || `Тест #${r.test_id}`);
    });
    return Array.from(map.entries())
      .map(([id, title]) => ({ id, title }))
      .sort((a, b) => a.title.localeCompare(b.title, dateLocale));
  }, [results]);

  const filtered = useMemo(() => {
    let res = results;
    if (testFilter !== 'all')
      res = res.filter(r => String(r.test_id) === testFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      res = res.filter(
        r =>
          (r.username || '').toLowerCase().includes(q) ||
          (r.participant_info?.name || '').toLowerCase().includes(q) ||
          (r.participant_info?.email || '').toLowerCase().includes(q),
      );
    }
    return res;
  }, [results, testFilter, search]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every(r => selected.has(r.id));
  const someSelected = filtered.some(r => selected.has(r.id));
  const selectedRows = filtered.filter(r => selected.has(r.id));
  const selectedCount = selectedRows.length;
  const selectedTestCount = new Set(selectedRows.map(r => r.test_id)).size;

  useEffect(() => {
    if (checkboxAllRef.current) {
      checkboxAllRef.current.indeterminate = someSelected && !allFilteredSelected;
    }
  }, [someSelected, allFilteredSelected]);

  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    const onScroll = () => setShowScrollTop(el.scrollTop > 200);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toastMsg) return;
    const timer = window.setTimeout(() => setToastMsg(''), 3500);
    return () => window.clearTimeout(timer);
  }, [toastMsg]);

  const toggleSelectAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach(r => next.delete(r.id));
      else filtered.forEach(r => next.add(r.id));
      return next;
    });
  };

  const toggleRow = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleCol = (col: ColKey) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      next.has(col) ? next.delete(col) : next.add(col);
      return next;
    });
  };

  const handlePublicResultsToggle = async () => {
    const next = !publicResultsEnabled;
    setPublicResultsLoading(true);
    try {
      const res = await setPublicResultsEnabled(next);
      setPublicResultsEnabledState(res.enabled);
    } catch {
      setToastMsg(t('resultsSettingsError'));
    } finally {
      setPublicResultsLoading(false);
    }
  };

  const getCell = (r: ResultRow, col: ColKey): string => {
    const pi = r.participant_info;
    switch (col) {
      case 'id': return String(r.id);
      case 'test': return r.test_title || '';
      case 'username': return r.username || '';
      case 'name': return pi?.name || '';
      case 'email': return pi?.email || '';
      case 'device': return pi?.device || '';
      case 'age': return pi?.age != null ? String(pi.age) : '';
      case 'gender': return pi?.gender || '';
      case 'time_spent': return r.time_spent != null ? String(r.time_spent) : '';
      case 'completed_at':
        return r.completed_at
          ? new Date(r.completed_at).toLocaleString(dateLocale)
          : '';
      case 'share_code': return r.share_code || '';
      case 'pdf':
        if (r.conclusion_exists) return t('resultsPdfDownload');
        return t('conclusionAbsentShort');
      case 'payment': return (r.paid || r.payment_status === 'paid') ? t('publishedYes') : t('publishedNo');
      case 'downloads': return `${r.conclusion_download_count ?? r.download_count ?? 0}/${r.conclusion_public_downloads ?? 0}`;
    }
  };

  const handleExportSelected = async () => {
    const ids = selectedRows.map(r => r.id);
    if (!ids.length) return;
    setExportLoading(true);
    try {
      await exportSelectedResults(ids, withParams);
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    const ids = selectedRows.map(r => r.id);
    if (!ids.length) return;
    setConfirmState({
      message: t('resultsDeleteConfirm').replace('{n}', String(ids.length)),
      onConfirm: async () => {
        setDeleteLoading(true);
        try {
          await deleteSelectedResults(ids);
          setResults(prev => prev.filter(r => !ids.includes(r.id)));
          setSelected(new Set());
        } catch {
          setToastMsg(t('resultsDeleteError'));
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  const viewDetails = async () => {
    const ids = filtered.filter(r => selected.has(r.id)).map(r => r.id);
    if (!ids.length) return;
    if (ids.length > 5) {
      setToastMsg(t('resultsTooManyAlert'));
      return;
    }
    const testIds = new Set(selectedRows.map(r => r.test_id));
    if (testIds.size > 1) {
      setToastMsg(t('resultsDiffTestsAlert'));
      return;
    }
    setDetailLoading(true);
    try {
      const data = await getResultsDetails(ids);
      setDetailData(data);
      setDetailMode(true);
    } catch {
      setToastMsg(t('resultsViewError'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleConclusion = async () => {
    const ids = selectedRows.map(r => r.id);
    if (!ids.length) return;
    setConclusionLoading(true);
    try {
      const type = ids.length === 1 ? 'individual' : 'comparison';
      const c = await createConclusion(ids, type);
      // Backend returns the existing conclusion for the same type and result set.
      navigate(`/conclusion/${c.id}`);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setToastMsg(detail || t('conclusionError'));
    } finally {
      setConclusionLoading(false);
    }
  };

  const cols = ALL_COLS.filter(c => visibleCols.has(c));

  if (loading) {
    return (
      <PageContainer>
        <StatusText>{t('resultsLoading')}</StatusText>
      </PageContainer>
    );
  }

  if (loadError) {
    return (
      <PageContainer>
        <StatusText>{loadError}</StatusText>
      </PageContainer>
    );
  }

  if (detailMode) {
    return (
      <PageContainer>
        <ResultDetailView data={detailData} onClose={() => setDetailMode(false)} />
      </PageContainer>
    );
  }

  return (
    <PageContainer onClick={() => showColMenu && setShowColMenu(false)}>
      <PageHeader>
        <Button variant="outline" size="sm" onClick={() => navigate('/tests')}>
          {t('btnBack')}
        </Button>
        <PageTitle>{t('resultsTitle')}</PageTitle>
      </PageHeader>

      <Controls onClick={e => e.stopPropagation()}>
        <StyledSelect
          value={testFilter}
          onChange={e => {
            setTestFilter(e.target.value);
            setSelected(new Set());
          }}
        >
          <option value="all">{t('resultsAllTests')} ({uniqueTests.length})</option>
          {uniqueTests.map(t => (
            <option key={t.id} value={String(t.id)}>
              {t.title}
            </option>
          ))}
        </StyledSelect>

        <SearchInput
          placeholder={t('resultsSearch')}
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setSelected(new Set());
          }}
        />

        <SettingToggle>
          <input
            type="checkbox"
            checked={publicResultsEnabled}
            disabled={publicResultsLoading}
            onChange={handlePublicResultsToggle}
          />
          {t('settingsPublicResults')}
        </SettingToggle>

        <ColToggleWrapper>
          <Button
            variant="outline"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              setShowColMenu(v => !v);
            }}
          >
            {t('resultsCols')}
          </Button>
          {showColMenu && (
            <ColToggleMenu onClick={e => e.stopPropagation()}>
              {ALL_COLS.map(col => (
                <ColToggleItem key={col}>
                  <input
                    type="checkbox"
                    checked={visibleCols.has(col)}
                    onChange={() => toggleCol(col)}
                  />
                  {COL_LABELS[col]}
                </ColToggleItem>
              ))}
            </ColToggleMenu>
          )}
        </ColToggleWrapper>
      </Controls>

      <TableWrapper ref={tableWrapperRef}>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  ref={checkboxAllRef}
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                />
              </Th>
              {cols.map(col => (
                <Th key={col}>{COL_LABELS[col]}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <Td
                  colSpan={cols.length + 1}
                  style={{ textAlign: 'center', padding: '2rem' }}
                >
                  {t('resultsEmpty')}
                </Td>
              </tr>
            ) : (
              filtered.map(r => (
                <Tr
                  key={r.id}
                  $selected={selected.has(r.id)}
                  onClick={() => toggleRow(r.id)}
                >
                  <Td onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleRow(r.id)}
                    />
                  </Td>
                  {cols.map(col => (
                    <Td key={col} title={col === 'pdf' && r.conclusion_exists ? t('resultsPdfDownload') : getCell(r, col)}>
                      {col === 'pdf' && r.conclusion_exists && r.conclusion_id ? (
                        <a
                          href={`/conclusion/${r.conclusion_id}`}
                          onClick={async e => {
                            e.stopPropagation();
                            e.preventDefault();
                            try {
                              // Гарантируем PDF: формируем при необходимости, затем скачиваем.
                              if (!r.pdf_ready) {
                                await saveConclusionPdf(r.conclusion_id!);
                              }
                              await downloadConclusionPdf(r.conclusion_id!);
                            } catch {
                              setToastMsg(t('resultsPdfDownloadError'));
                            }
                          }}
                          style={{ color: '#38a169' }}
                        >
                          {t('resultsPdfDownload')}
                        </a>
                      ) : getCell(r, col)}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrapper>

      <ScrollTopButton
        $visible={showScrollTop}
        onClick={() => {
          if (tableWrapperRef.current) tableWrapperRef.current.scrollTop = 0;
        }}
        title={t('scrollTopTitle')}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="18 15 12 9 6 15"/>
        </svg>
      </ScrollTopButton>

      <FixedBottomBar>
        <StatusText>
          {t('resultsShown')}: {filtered.length} {t('resultsFrom')} {totalResults} &nbsp;|&nbsp; {t('resultsSelectedCount')}:{' '}
          {selectedCount}
          {results.length < totalResults && (
            <> &nbsp;|&nbsp; <span style={{ color: '#f59e0b' }}>{t('resultsPartialLoad').replace('{n}', String(results.length)).replace('{total}', String(totalResults))}</span></>
          )}
        </StatusText>
        {results.length < totalResults && (
          <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore ? t('genLoading') : t('resultsLoadMore')}
          </Button>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={withParams}
            onChange={e => setWithParams(e.target.checked)}
          />
          {t('resultsWithParams')}
        </label>
        <Button
          variant="primary"
          size="sm"
          onClick={handleExportSelected}
          disabled={selectedCount === 0 || exportLoading}
        >
          {exportLoading ? t('genLoading') : `${t('resultsExportBtn')} (${selectedCount})`}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDeleteSelected}
          disabled={selectedCount === 0 || deleteLoading}
          style={{ color: '#e53e3e', borderColor: '#e53e3e' }}
        >
          {deleteLoading ? t('resultsDeletingLabel') : `${t('resultsDeleteBtn')} (${selectedCount})`}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={viewDetails}
          disabled={selectedCount === 0 || selectedCount > 5 || selectedTestCount > 1 || detailLoading}
          title={
            selectedCount > 5
              ? t('resultsTipMax5')
              : selectedTestCount > 1
                ? t('resultsTipOneTest')
                : undefined
          }
        >
          {detailLoading
            ? t('genLoading')
            : selectedCount > 5
              ? t('resultsViewTooMany')
              : selectedTestCount > 1
                ? t('resultsViewDiffTests')
                : `${t('resultsViewBtn')} (${selectedCount})`}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleConclusion}
          disabled={selectedCount === 0 || conclusionLoading}
        >
          {conclusionLoading ? t('conclusionLoading') : `${t('conclusionGetBtn')} (${selectedCount})`}
        </Button>
      </FixedBottomBar>

      {/* ── Toast ── */}
      {toastMsg && <ToastMsg>{toastMsg}</ToastMsg>}

      {/* ── Confirm modal ── */}
      {confirmState && (
        <Overlay onClick={() => setConfirmState(null)}>
          <ConfirmModal onClick={e => e.stopPropagation()}>
            <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>{confirmState.message}</p>
            <ConfirmActions>
              <Button variant="outline" size="sm" onClick={() => setConfirmState(null)}>
                {t('genCancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  confirmState.onConfirm();
                  setConfirmState(null);
                }}
              >
                {t('conclusionsConfirmAction')}
              </Button>
            </ConfirmActions>
          </ConfirmModal>
        </Overlay>
      )}
    </PageContainer>
  );
};

export default ResultsPage;
